import QRCode from "react-qr-code";

// Target rendered size (px) of the counter QR. The actual size is rounded up
// to an exact multiple of the module count so every module lands on whole
// pixels and the browser does not have to anti-alias the module edges.
const TARGET_QR_PX = 300;

// Byte-mode data capacity (bytes) per QR version at error-correction level "L"
// (react-qr-code's default). Index 0 = version 1. The payloads here are ASCII
// URLs, and the library sizes the version from the string length while writing
// each character as one byte (charCodeAt(i) & 0xFF), so the byte length of an
// ASCII payload and its length agree. Non-ASCII text would need the byte count.
const QR_BYTE_CAPACITY_L = [
  17, 32, 53, 78, 106, 134, 154, 192, 230, 271, 321, 367, 425, 458, 520, 586,
  644, 718, 792, 858, 929, 1003, 1091, 1171, 1273, 1367, 1465, 1528, 1628, 1732,
  1840, 1952, 2068, 2188, 2303, 2431, 2563, 2699, 2809, 2953,
];

/**
 * @param {object} params
 * @param {OnlineOrder} params.onlineOrder
 * @param {"md" | "lg"} params.size
 * @returns {React.Component}
 */
const OnlineOrderPaymentQrCode = ({ onlineOrder, size }) => {
  // Encode only the order id. The order landing page fetches the rest
  // (`minutesAmount`, `priceRub`, `phoneNumber`, `paymentUrl`) from the API.
  // Keep this payload minimal on purpose: every extra param pushes the QR to a
  // higher version with more, smaller modules, which is what made iPhones
  // unable to decode it (see #670 follow-up). Do not add params back.
  const url = `${window.location.origin}/order?orderId=${onlineOrder.id}`;

  if (size !== "md" && size !== "lg") {
    throw new Error(`Unknown size=${size}`);
  }

  const moduleCount = qrModuleCount(url);
  // md: smallest size >= TARGET_QR_PX that is an exact multiple of the module
  //     count (33 modules -> 10 px/module = 330 px; 37 -> 9 px = 333 px).
  // lg: keep the 500 px media-screen size; only its quiet zone is widened.
  const renderSizePx =
    size === "md" ? moduleCount * Math.ceil(TARGET_QR_PX / moduleCount) : 500;
  console.info(
    `Online order QR: payload=${url.length} chars, modules=${moduleCount}, ` +
      `render=${renderSizePx}px, ${(renderSizePx / moduleCount).toFixed(2)}px/module`,
  );

  // p-10 = 40 px quiet zone: 4 modules for a 33-module code at 10 px/module.
  // p-16 = 64 px quiet zone: >4 modules for both 33- and 37-module codes.
  const containerClass =
    size === "md" ? "p-10 rounded-xl" : "p-16 rounded-[50px]";

  return (
    <div className={`${containerClass} bg-white w-fit max-w-full mx-auto`}>
      <QRCode
        size={renderSizePx}
        value={url}
        level="L"
        fgColor="#000"
        bgColor="#fff"
        style={{ maxWidth: "100%", height: "auto" }}
      />
    </div>
  );
};

export default OnlineOrderPaymentQrCode;

/**
 * Module count of the QR code that react-qr-code will render for `value`.
 * @param {string} value
 * @returns {number}
 */
function qrModuleCount(value) {
  const byteLength = new TextEncoder().encode(value).length;
  const versionIndex = QR_BYTE_CAPACITY_L.findIndex((cap) => byteLength <= cap);
  if (versionIndex === -1) {
    throw new Error(`QR payload too large: ${byteLength} bytes`);
  }
  return (versionIndex + 1) * 4 + 17;
}
